/**
 * Pure time-based critically-damped spring animator for the engine
 * dim alpha uniform. Parallel implementations on iOS (Swift) and
 * Android (Kotlin) must use identical OMEGA and settle threshold —
 * load-bearing cross-platform. See spec §5.3.
 *
 * Settle response: `x(t) = target + (start - target) · (1 + ω·t) · e^(-ω·t)`
 */

export const OMEGA = 10
export const SETTLE_THRESHOLD = 0.002

export interface DimAlphaAnimator {
  readonly currentAlpha: number
  readonly isSettled: boolean
  setTarget(target: number, nowSec: number): void
  tick(nowSec: number): number
}

export function createDimAlphaAnimator(): DimAlphaAnimator {
  let startAlpha = 0
  let targetAlpha = 0
  let startTime = 0
  let current = 0

  const isSettled = (): boolean => Math.abs(current - targetAlpha) < SETTLE_THRESHOLD

  return {
    get currentAlpha() {
      return current
    },
    get isSettled() {
      return isSettled()
    },
    setTarget(target, nowSec) {
      startAlpha = current
      targetAlpha = target
      startTime = nowSec
    },
    tick(nowSec) {
      if (isSettled()) {
        current = targetAlpha
        return current
      }
      const dt = Math.max(0, nowSec - startTime)
      const omegaT = OMEGA * dt
      const decay = (1 + omegaT) * Math.exp(-omegaT)
      current = targetAlpha + (startAlpha - targetAlpha) * decay
      return current
    },
  }
}
